
import {
  Vue,
  Component,
  Prop,
  ModelSync,
  Inject
} from "vue-property-decorator";
import { Control, OxygenControlsTreeContext } from "@/oxygen/classes/lib/types";
import AlignGrid from "@/oxygen/classes/inputs/align/AlignGrid.vue";
import { AlignInputValue } from "@/oxygen/classes/lib/align";
import lodash from "lodash";
import { uiStore } from "@/store/store-accessor";
import {
  controlValueToPlaceholder,
  shouldShowInheritanceAsPlaceholder
} from "@/oxygen/classes/lib/inheritance";

@Component({
  components: {
    AlignGrid
  }
})
export default class OxygenAlignInput extends Vue {
  @Prop()
  control!: Control;

  @Prop()
  path!: string;

  @ModelSync("value", "input", {
    default: () => ({})
  })
  syncedValue!: AlignInputValue;

  @Inject({ from: "oxyContext" })
  readonly context!: OxygenControlsTreeContext;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.OxygenControl =
      require("@/oxygen/classes/OxygenControl.vue").default;
  }

  verticalAlignment = [
    { value: "start", label: "Left" },
    { value: "center", label: "Center" },
    { value: "end", label: "Right" }
  ];

  horizontalAlignment = [
    { value: "start", label: "Top" },
    { value: "center", label: "Center" },
    { value: "end", label: "Bottom" }
  ];

  positionAlignment = [
    { value: "stretch", label: "Stretch" },
    { value: "baseline", label: "Baseline" }
  ];

  distributionAlignment = [
    { value: "space-between", label: "Space Between" },
    { value: "space-around", label: "Space Around" }
  ];

  get parentInheritance() {
    return this.context.getParentControlInheritance(this.path);
  }

  get alignPlaceholder(): AlignInputValue | null {
    const primaryAxis = this.parentInheritance?.["flex_align.primary_axis"];
    const crossAxis = this.parentInheritance?.["flex_align.cross_axis"];

    const inherited: Partial<AlignInputValue> = {};

    if (shouldShowInheritanceAsPlaceholder(primaryAxis)) {
      inherited["primary_axis"] = primaryAxis.value as string | undefined;
    }

    if (shouldShowInheritanceAsPlaceholder(crossAxis)) {
      inherited["cross_axis"] = crossAxis.value as string | undefined;
    }

    if (Object.keys(inherited).length === 0) return null;
    return inherited as AlignInputValue;
  }

  get layout() {
    const inheritance = this.parentInheritance;

    return {
      display: inheritance?.display?.value as string | undefined,
      flex_direction: inheritance?.flex_direction?.value as string | undefined
    };
  }

  get breakpoint() {
    return uiStore.currentBreakpoint.id;
  }

  get display() {
    return this.layout?.display || "flex";
  }

  get direction() {
    if (this.display === "grid") return "column";
    return this.layout?.flex_direction || "row";
  }

  get isFlexRow() {
    return this.display === "flex" && ["row", "wrap"].includes(this.direction);
  }

  get primaryControl(): Control {
    const items = this.isFlexRow
      ? [...this.verticalAlignment, ...this.distributionAlignment]
      : [...this.verticalAlignment, ...this.positionAlignment];

    const slug = this.isFlexRow ? "primary_axis" : "cross_axis";

    const control: Control = {
      slug,
      name: "X",
      type: "select",
      forcePlaceholder: true,
      items
    };

    const primaryPlaceholder = controlValueToPlaceholder(
      control,
      this.alignPlaceholder?.primary_axis
    );

    const crossPlaceholder = controlValueToPlaceholder(
      control,
      this.alignPlaceholder?.cross_axis
    );

    control.placeholder = this.isFlexRow
      ? primaryPlaceholder
      : crossPlaceholder;

    return control;
  }

  get crossControl(): Control {
    const invert = this.isFlexRow || this.display === "grid";
    const items = invert
      ? [...this.horizontalAlignment, ...this.positionAlignment]
      : [...this.horizontalAlignment, ...this.distributionAlignment];

    const slug = this.isFlexRow ? "cross_axis" : "primary_axis";

    const control: Control = {
      slug,
      name: "Y",
      type: "select",
      forcePlaceholder: true,
      items
    };

    const crossPlaceholder = controlValueToPlaceholder(
      control,
      this.alignPlaceholder?.cross_axis
    );

    const primaryPlaceholder = controlValueToPlaceholder(
      control,
      this.alignPlaceholder?.primary_axis
    );

    control.placeholder = invert ? crossPlaceholder : primaryPlaceholder;

    return control;
  }

  updateInnerValue(path: string, value: string) {
    this.syncedValue = lodash.set(this.syncedValue, path, value);
  }
}
